// Shows a book overlay with a left and right page.
// Can provide the exact content for each page, or a list of phrases to split between the two pages.
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import './BookOverlay.scss';

// Mock imports for SVGs
import TopLeftSVG from '../../../images/BookOverlay/bookTopLeft.svg';
import TopRightSVG from '../../../images/BookOverlay/bookTopRight.svg';
import CloseButtonSVG from '../../../images/icons/closeButton.svg';
import Portal from '../Portal';
import { isiPad } from '../../../services/environment';
import FaArrowRight from 'react-icons/lib/fa/arrow-right';

const BookOverlay = ({ leftPageContent, rightPageContent, onClose, phrases, showNonverbalBookOverlayContinueButton, onClickNonverbalBookOverlayButton, closeOnEscapeOrOutsideClick }) => {

  const [leftPage, setLeftPage] = useState(leftPageContent);
  const [rightPage, setRightPage] = useState(rightPageContent);

  const splitPhrases = (phrases) => {
    // Calculate the total number of characters
    const totalLength = phrases.reduce((sum, str) => sum + str.length, 0);
    
    // Target length is half of the total length
    const target = totalLength / 2;
  
    let runningTotal = 0;
    let splitIndex = 0;
    let minDifference = Infinity;
  
    for (let i = 0; i < phrases.length; i++) {
      runningTotal += phrases[i].length;
      const currentDifference = Math.abs(target - runningTotal);
  
      if (currentDifference < minDifference) {
        minDifference = currentDifference;
        splitIndex = i;
      }
    }
  
    // Concatenate the first part for X and the second part for Y
    const X = phrases.slice(0, splitIndex + 1).join("\n\n");
    const Y = phrases.slice(splitIndex + 1).join("\n\n");

    setLeftPage(X);
    setRightPage(Y);
  }

  const showCloseButton = !showNonverbalBookOverlayContinueButton;
  const canDismiss = closeOnEscapeOrOutsideClick && showCloseButton;

  useEffect(() => {
    if (phrases && phrases.length > 0) {
      splitPhrases(phrases);
    } else {
      setLeftPage(leftPageContent);
      setRightPage(rightPageContent);
    }
  }, [leftPageContent, rightPageContent, phrases]);

  useEffect(() => {
    if (!canDismiss) return undefined;

    const handleKeyDown = (event) => {
      if (event.repeat) return;
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canDismiss, onClose]);

  return (
    <Portal>
        {canDismiss && (
          <div className="book-overlay-backdrop" onClick={onClose} />
        )}
        <div className={cx('book-overlay', { ipad: isiPad(), dismissible: canDismiss })}>
          <div className="book-column left">
            <img src={TopLeftSVG} alt="Top Left Page Curve" className="page-curve"/>
            <div className="page-content">
              <div className={`text-content ${showNonverbalBookOverlayContinueButton ? 'nonverbal' : ''}`}>{leftPage}</div>
            </div>
          </div>
          <div className="book-column right">
            {showCloseButton && (
              <button className="close-button" onClick={onClose}>
                <img src={CloseButtonSVG} alt="Close" />
              </button>
            )}
            <img src={TopRightSVG} alt="Top Right Page Curve" className="page-curve"/>
            <div className="page-content">
              <div className={`text-content ${showNonverbalBookOverlayContinueButton ? 'nonverbal' : ''}`}>{rightPage}</div>
            </div>
            {showNonverbalBookOverlayContinueButton && (
              <button className="nonverbal-close-button" onClick={onClickNonverbalBookOverlayButton}>
                <FaArrowRight size="70" className="forward buttonSvg"/>
              </button>
            )}
          </div>
        </div>
    </Portal>
  );
};

BookOverlay.propTypes = {
  leftPageContent: PropTypes.node,
  rightPageContent: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  phrases: PropTypes.array,
  showNonverbalBookOverlayContinueButton: PropTypes.bool,
  onClickNonverbalBookOverlayButton: PropTypes.func,
  closeOnEscapeOrOutsideClick: PropTypes.bool,
};

export default BookOverlay;
